// Build a Builder canvas dynamically from a regular agent's flow_config.
//
// Replaces the static 9-node `INITIAL_NODES` fallback for agents that
// don't have a saved canvas (e.g., bootstrap-* installed agents,
// use-case-installed expert agents, agents created via the API
// without ever opening the Builder). The canvas should reflect what
// the agent ACTUALLY does, not a fixed pipeline shape.
//
// Heuristic: trigger + LLM + action are mandatory (every agent has
// them). Other nodes render only when the corresponding flow_config
// section is enabled — so an agent with knowledge=false / memory=
// false / api=false collapses to trigger → LLM → action, and an
// agent with everything enabled renders the full pipeline.
//
// For supervisor agents (`agent_kind === 'supervisor'`) the
// supervisor bridge's ORCHESTRATION_TEMPLATES path takes precedence
// in BuilderView; this helper handles the regular case only.

import { COLORS } from "../../theme/colors";

// Lay nodes out left-to-right with this horizontal pitch.
const X_PITCH = 190;
const X_START = 60;
const Y_MAIN = 260;
const Y_TOP = 120;     // for parallel nodes above the main line (knowledge / grounding)
const Y_BOTTOM = 400;  // for parallel nodes below the main line (memory)

// Node specs keyed by type — mirrors the palette in constants.js.
const NODE_SPEC = {
  trigger:           { label: "Trigger",        icon: "\u26A1",  color: COLORS.orange },
  preprocess:        { label: "Pre-Process",    icon: "\u2699",  color: COLORS.orange },
  input_guardrails:  { label: "Input Guard",    icon: "\u{1F6E1}", color: COLORS.pink },
  knowledge:         { label: "Knowledge Base", icon: "\u{1F4DA}", color: COLORS.cyan },
  grounding:         { label: "Grounding",      icon: "\u{1F50D}", color: COLORS.green },
  memory:            { label: "Memory",         icon: "\u{1F4BE}", color: COLORS.cyan },
  llm:               { label: "LLM Engine",     icon: "\u{1F9E0}", color: COLORS.accentText },
  api:               { label: "API / Tools",    icon: "\u{1F517}", color: COLORS.pink },
  internal_tools:    { label: "Internal Tools", icon: "\u{1F3ED}", color: COLORS.accentText },
  output_guardrails: { label: "Output Guard",   icon: "\u2714",  color: COLORS.green },
  fact_checker:      { label: "Fact Check",     icon: "\u{1F50E}", color: COLORS.green },
  action:            { label: "Action",         icon: "\u2197",  color: COLORS.green },
};

const mkNode = (type, x, y) => ({
  id: `${type}-1`,
  type,
  ...NODE_SPEC[type],
  x, y,
});

// True iff the flow_config section is meaningfully populated.
// Heuristics tuned to v6+ flow_config shapes seen on staging.
function _isEnabled(fc, key) {
  const sec = fc?.[key];
  if (!sec) return false;
  if (typeof sec !== "object") return false;
  // Explicit `enabled: false` wins.
  if (sec.enabled === false) return false;
  // Explicit `enabled: true` wins.
  if (sec.enabled === true) return true;
  // No `enabled` field but section has substance → treat as on.
  // (Some sections like ``preprocess`` carry nested toggles
  // ``language_detection.enabled`` instead of a top-level ``enabled``.)
  return Object.keys(sec).length > 0;
}

function _hasInternalTools(fc) {
  const tools = fc?.api?.internal_toolsets;
  return Array.isArray(tools) && tools.length > 0;
}

/**
 * Build {nodes, connections} from a regular agent's flow_config.
 *
 * - trigger / llm / action are always present.
 * - input_guardrails / preprocess / output_guardrails / fact_checker
 *   appear only when their flow_config section is enabled.
 * - knowledge / grounding / memory render in parallel above/below the
 *   main horizontal line when enabled.
 * - api renders when api.enabled === true OR internal_toolsets are
 *   configured. internal_tools appears alongside api when toolsets
 *   are present (visual nuance).
 * - Sub-agent (`agent`) nodes are NOT auto-rendered here — Dynamic
 *   Agent invoke chains are computed at runtime in graph_nodes, not
 *   declared in flow_config. The Supervisor template path handles
 *   the multi-expert case.
 */
export function buildRegularCanvas(agent) {
  const fc = agent?.flow_config || {};
  const nodes = [];
  const connections = [];
  let x = X_START;

  // 1. Trigger (always)
  nodes.push(mkNode("trigger", x, Y_MAIN));
  let prev = "trigger-1";
  x += X_PITCH;

  // 2. Preprocess (when language_detection or any preprocess sub-config is present)
  if (_isEnabled(fc, "preprocess")) {
    nodes.push(mkNode("preprocess", x, Y_MAIN));
    connections.push({ from: prev, to: "preprocess-1" });
    prev = "preprocess-1";
    x += X_PITCH;
  }

  // 3. Input guardrails (when enabled)
  if (_isEnabled(fc, "input_guardrails")) {
    nodes.push(mkNode("input_guardrails", x, Y_MAIN));
    connections.push({ from: prev, to: "input_guardrails-1" });
    prev = "input_guardrails-1";
    x += X_PITCH;
  }

  // 4. Retrieval lane (knowledge / grounding above; memory below)
  // — these run in parallel before the LLM.
  const llmX = x + X_PITCH;
  const retrievalSlot = x;
  let retrievalRendered = false;

  if (_isEnabled(fc, "knowledge")) {
    nodes.push(mkNode("knowledge", retrievalSlot, Y_TOP));
    connections.push({ from: prev, to: "knowledge-1" });
    retrievalRendered = true;
  }
  if (_isEnabled(fc, "grounding")) {
    // Slight x-offset when both knowledge + grounding render so they
    // don't stack directly on top of each other.
    const gx = retrievalRendered ? retrievalSlot + 90 : retrievalSlot;
    const gy = retrievalRendered ? Y_TOP - 60 : Y_TOP;
    nodes.push({ ...mkNode("grounding", gx, gy) });
    connections.push({ from: prev, to: "grounding-1" });
    retrievalRendered = true;
  }
  if (_isEnabled(fc, "memory")) {
    nodes.push(mkNode("memory", retrievalSlot, Y_BOTTOM));
    connections.push({ from: prev, to: "memory-1" });
    retrievalRendered = true;
  }
  if (retrievalRendered) {
    x = llmX;
  }

  // 5. LLM (always)
  nodes.push(mkNode("llm", x, Y_MAIN));
  // Connect retrieval lane → LLM if any retrieval rendered, else
  // direct from prev → LLM.
  if (retrievalRendered) {
    if (nodes.some((n) => n.id === "knowledge-1")) connections.push({ from: "knowledge-1", to: "llm-1" });
    if (nodes.some((n) => n.id === "grounding-1")) connections.push({ from: "grounding-1", to: "llm-1" });
    if (nodes.some((n) => n.id === "memory-1"))    connections.push({ from: "memory-1", to: "llm-1" });
  } else {
    connections.push({ from: prev, to: "llm-1" });
  }
  prev = "llm-1";
  x += X_PITCH;

  // 6. API / Tools (when api.enabled OR internal_toolsets configured).
  // API/Tools is a SIDE BRANCH off the LLM (call-tool → return-result
  // loop), NOT a step in the main chain. Keep `prev` on the LLM so the
  // main flow continues from LLM → output_guard / action — otherwise
  // disabling output_guardrails would silently chain API → Action.
  if (_isEnabled(fc, "api") || _hasInternalTools(fc)) {
    // Render above the main line as a side node.
    nodes.push(mkNode("api", x, Y_TOP));
    connections.push({ from: "llm-1", to: "api-1" });
    connections.push({ from: "api-1", to: "llm-1", type: "loop", label: "Tool results" });
    x += X_PITCH;
  }

  // 7. Fact-checker (between LLM/api and output_guardrails when enabled).
  if (_isEnabled(fc, "fact_checker")) {
    nodes.push(mkNode("fact_checker", x, Y_MAIN));
    connections.push({ from: prev, to: "fact_checker-1" });
    prev = "fact_checker-1";
    x += X_PITCH;
  }

  // 8. Output guardrails (when enabled).
  if (_isEnabled(fc, "output_guardrails")) {
    nodes.push(mkNode("output_guardrails", x, Y_MAIN));
    connections.push({ from: prev, to: "output_guardrails-1" });
    prev = "output_guardrails-1";
    x += X_PITCH;
  }

  // 9. Action (always)
  nodes.push(mkNode("action", x, Y_MAIN));
  connections.push({ from: prev, to: "action-1" });

  return { nodes, connections };
}
